import React, { useEffect, useRef, useState } from "react"
import { Chart, BarElement, BarController, CategoryScale, LinearScale, Tooltip } from "chart.js"
import type { SeriesSummary } from "~/api/portfolio_summaries"
import SegToggle from "./SegToggle"
import { formatCurrency, POSITIVE_COLOR, NEGATIVE_COLOR } from "./utils"

Chart.register(BarElement, BarController, CategoryScale, LinearScale, Tooltip)

interface SeriesPnlChartProps {
  series: SeriesSummary[]
}

type PnlKey = "qoq" | "itd"

const SeriesPnlChart: React.FC<SeriesPnlChartProps> = ({ series }) => {
  const [key, setKey] = useState<PnlKey>("qoq")
  const chartRef = useRef<HTMLCanvasElement>(null)
  const chartInstanceRef = useRef<Chart<"bar"> | null>(null)

  useEffect(() => {
    if (!chartRef.current) return

    const sorted = [...series].sort((a, b) => {
      const av = key === "qoq" ? a.qoq_pl : a.itd_pl
      const bv = key === "qoq" ? b.qoq_pl : b.itd_pl
      return bv - av
    })
    const labels = sorted.map((s) => s.name)
    const values = sorted.map((s) => (key === "qoq" ? s.qoq_pl : s.itd_pl))
    const colors = values.map((v) => (v >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR))

    if (chartInstanceRef.current) chartInstanceRef.current.destroy()
    const ctx = chartRef.current.getContext("2d")
    if (!ctx) return

    chartInstanceRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderRadius: 2,
            maxBarThickness: 14,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (c) => ` ${c.label}: ${formatCurrency(c.parsed.y)}`,
            },
          },
        },
        scales: {
          x: {
            ticks: { autoSkip: true, maxRotation: 90, minRotation: 90, font: { size: 9 } },
            grid: { display: false },
          },
          y: {
            grid: { color: "#eef0f2" },
            ticks: { callback: (v) => formatCurrency(v as number) },
          },
        },
      },
    })

    return () => {
      chartInstanceRef.current?.destroy()
      chartInstanceRef.current = null
    }
  }, [series, key])

  return (
    <div className="b-portfolio-summary-card">
      <div className="b-portfolio-summary-card-header">
        <div>
          <h2>P&amp;L by series</h2>
          <div className="b-portfolio-summary-card-sub">
            {key === "qoq" ? "Quarter-over-quarter" : "Inception-to-date"} ·{" "}
            {series.length} series
          </div>
        </div>
        <SegToggle
          options={[
            { key: "qoq", label: "QoQ P&L" },
            { key: "itd", label: "ITD P&L" },
          ]}
          active={key}
          onChange={(value) => setKey(value as PnlKey)}
        />
      </div>
      <div className="b-portfolio-summary-card-body">
        <div className="b-portfolio-summary-chart-wrap b-portfolio-summary-chart-wrap--tall">
          <canvas ref={chartRef} />
        </div>
      </div>
    </div>
  )
}

export default SeriesPnlChart
